import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { GoldButton } from "./GoldButton";
import { PhoneMockup } from "./PhoneMockup";
import { EASE } from "./Reveal";

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="https://images.pexels.com/photos/5648779/pexels-photo-5648779.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920"
          alt=""
          className="h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-night/70 via-night/85 to-night" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto grid max-w-7xl gap-16 px-5 pb-28 pt-36 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-8 lg:pb-36 lg:pt-44">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold"
            data-testid="hero-overheadline"
          >
            <span className="h-px w-10 bg-gold/60" />
            Sfida gratuita · 40 giorni · Direttamente nell'app
          </motion.p>

          <h1 className="mt-8 font-display text-4xl font-bold uppercase leading-[1.02] tracking-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <HeroLine delay={0.45}>Non ti serve un altro</HeroLine>
            <HeroLine delay={0.58}>contenuto cristiano</HeroLine>
            <HeroLine delay={0.71}>da salvare.</HeroLine>
            <HeroLine delay={0.92}>
              <span className="text-gold">Ti servono 40 giorni</span>
            </HeroLine>
            <HeroLine delay={1.05}>
              <span className="text-gold">per tornare a camminare.</span>
            </HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: EASE }}
            className="mt-8 font-display text-sm font-bold uppercase tracking-[0.35em] text-ivory/80"
            data-testid="hero-pillars"
          >
            Fede<span className="text-gold">.</span> Mente<span className="text-gold">.</span> Corpo<span className="text-gold">.</span> Disciplina<span className="text-gold">.</span>
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ivory/60 sm:text-lg"
            data-testid="hero-subtext"
          >
            Entra nella Sfida del Deserto e ricevi ogni giorno una direzione concreta per riportare
            Dio al centro della tua quotidianità — gratuitamente.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.65, ease: EASE }}
            className="mt-10"
          >
            <GoldButton testId="hero-cta-button" size="lg" />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-ivory/40" data-testid="hero-microcopy">
              Nessun abbonamento. Nessuna carta richiesta.
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.9 }}
            className="mt-10 border-t border-ivory/10 pt-6 text-xs uppercase tracking-[0.2em] text-ivory/50"
            data-testid="hero-path"
          >
            40 giorni guidati <span className="text-gold">→</span> 1 percorso quotidiano <span className="text-gold">→</span> il primo passo verso Tempio Vivo
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 80, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.4, delay: 1.1, ease: EASE }}
          className="relative mx-auto w-full max-w-[300px] lg:max-w-[320px]"
          style={{ y: phoneY }}
        >
          <PhoneMockup screen="day" />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ChevronDown className="h-5 w-5 text-gold/70" />
        </motion.div>
      </motion.div>
    </section>
  );
};
